// IMPORTANT: Import global less first to avoid CSS cascading issues
import '../less/_app.global.less';

import 'src/lib/polyfills';
import 'src/lib-server/django-shims';
import {QueryClient, QueryClientProvider, Hydrate} from '@tanstack/react-query';
import {useStaticRendering} from 'mobx-react-lite';
import NextApp, {AppContext} from 'next/app';
import {useEffect, useState} from 'react';

import {CommonStoresProvider} from '@udemy/common-stores';
import {detectKeyboardNavigation} from '@udemy/design-system-utils';
import {I18nProvider, resolveLocaleDirection, useI18n, UdemyLocale} from '@udemy/i18n';
import {
    APP_CONTEXT_VAR_NAME,
    getClientAppContext,
    mergeWithArrayOverwrite,
} from '@udemy/nextjs-core';
import {RENDER_MODES} from '@udemy/nextjs-core';
import {shimUDPerformance} from '@udemy/performance-rum-client';
import {FbtBundleConfigContext} from '@udemy/react-discovery-units';
import {ExperimentationProvider} from '@udemy/react-experimentation-provider';
import {PERSONAL_PLAN_HARD_OPT_IN_MODAL_FEATURE} from '@udemy/react-subscription-experience';
import {ShoppingClient} from '@udemy/shopping';
import {StoreProvider} from '@udemy/store-provider';
import {udApi} from '@udemy/ud-api';
import {UDData, UDDataConfig, UDDataProvider, useUDData, useUDLink} from '@udemy/ud-data';

import {i18nPathToBundleMap, i18nPreferred} from 'next.config.i18n';
import {BindI18nGlobals} from 'src/components/bind-i18n-globals/bind-i18n-globals';
import {AppWithLayoutProps} from 'src/components/layout/types';
import {useDevice} from 'src/hooks/useDevice';
import {
    CART_LOGINSIGNUP_REDIRECT_TO_CHECKOUT,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    COURSE_GUIDANCE_FEATURE,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    DESKTOP_POST_ADD_TO_CART_BEHAVIOR,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    JBI_ENHANCED_LECTURE_UNIT,
    SHOW_AT_RISK_FEEDBACK_SURVEY,
    LOCALE_SPECIFIC_TOPICS,
    USER_STREAKS_ON_MX_LIHP,
    LIHP_CERT_VOUCHER_RECO_BANNER,
    TOP_10_COURSES_JAPAN,
    NEW_SEARCH_BAR_MX,
    FBT_FORCE_GO_TO_CART,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    VALUE_PLAN_STARTER_PLAN,
    VALUE_PLAN_UPSELL,
    FBT_BUNDLE_SERVICE_VOUCHERS,
    PP_MODULE_POSITIONING,
    LIHP_CAROUSEL_HOVER_POPUP_REMOVAL,
    POPULAR_CATEGORIES_MODULE,
    LIHP_MARKETING_MESSAGING_MODULES,
} from 'src/lib/experimentation-constants';
import {PAGE_KEYS} from 'src/pages/constants';
import {IntegrationsStore} from 'src/stores/integrations-store';
import {RootStore} from 'src/stores/root-store';

// Requests are proxied through Next app, so use relative URL for API calls
const DEPLOY_ENV = process.env.DEPLOY_ENV;
if (DEPLOY_ENV === 'local' || DEPLOY_ENV === 'dev') {
    udApi.configure({
        getBaseUrl: () => `${process.env.APP_BASE_PATH}api-2.0`,
    });
} else if (typeof window !== 'undefined' && window.PUBLIC_CONFIG?.MONOLITH_URL) {
    const {MONOLITH_URL} = window.PUBLIC_CONFIG;
    udApi.configure({
        getBaseUrl: () => `${MONOLITH_URL}/api-2.0`,
    });
}

// TODO: rehydrate from pageProps instead
const queryClient = new QueryClient();

/**
 * Prevent observable components from re-rendering on the server. Otherwise this causes a memory leak since lots of mobx
 * references are being created on the server and never disposed.
 * @see https://github.com/mobxjs/mobx/tree/main/packages/mobx-react-lite#enablestaticrenderingenable-true
 */
// eslint-disable-next-line react-hooks/rules-of-hooks
useStaticRendering(typeof window === 'undefined');

const EXPERIMENTATION_FEATURES = [
    CART_LOGINSIGNUP_REDIRECT_TO_CHECKOUT,
    COURSE_CARDS_OPEN_IN_NEW_TAB,
    COURSE_GUIDANCE_FEATURE,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    DESKTOP_POST_ADD_TO_CART_BEHAVIOR,
    FBT_FORCE_GO_TO_CART,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    LIHP_CERT_VOUCHER_RECO_BANNER,
    TOP_10_COURSES_JAPAN,
    LOCALE_SPECIFIC_TOPICS,
    NEW_SEARCH_BAR_MX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    PERSONAL_PLAN_HARD_OPT_IN_MODAL_FEATURE,
    POPULAR_CATEGORIES_MODULE,
    USER_STREAKS_ON_MX_LIHP,
    VALUE_PLAN_STARTER_PLAN,
    VALUE_PLAN_UPSELL,
    FBT_BUNDLE_SERVICE_VOUCHERS,
    PP_MODULE_POSITIONING,
    LIHP_CAROUSEL_HOVER_POPUP_REMOVAL,
    LIHP_MARKETING_MESSAGING_MODULES,
];

/**
 * Injects the FBT bundle redesign config into context. The cart success modal
 * builds the BundleUnit internally, so config flows via context rather than props.
 */
const FbtBundleConfigProvider = ({children}: {children: React.ReactNode}) => (
    <FbtBundleConfigContext.Provider
        value={{
            excludeAddedCourseFromBundle: true,
            showPerCardAddToCart: true,
            hideRelatedLabelsList: true,
            wideSuccessModal: true, // wider modal (maps to CartSuccessModal size='wide')
        }}
    >
        {children}
    </FbtBundleConfigContext.Provider>
);

const I18nAccessBootstrapper = () => {
    const i18n = useI18n();
    const udData = useUDData();
    const udLink = useUDLink();

    useEffect(() => {
        if (!udData.isGlobalMeContextLoading) {
            ShoppingClient.initialize({
                i18n,
                udLink,
                isShoppingCartFeatureEnabled: udData.Config.features.shopping_cart,
            });

            if (udData.me.is_authenticated && udData.me.id) {
                ShoppingClient.setUserId(udData.me.id);
            }

            ShoppingClient.fetch();
        }
    }, [i18n, udData, udLink]);

    return null;
};

export default function App({
    Component,
    locale, // NOTE: this is just the language portion of the locale, i.e. en instead of en_US
    pageProps,
}: AppWithLayoutProps) {
    // Get application data and render mode
    const {appContext, pageKey, renderMode, priorityData} = pageProps;

    const experimentationFeatures =
        pageKey === PAGE_KEYS.PERSONAL_PLAN_LOGGED_IN_HOME
            ? [...EXPERIMENTATION_FEATURES, JBI_ENHANCED_LECTURE_UNIT, SHOW_AT_RISK_FEEDBACK_SURVEY]
            : EXPERIMENTATION_FEATURES;

    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';

    /**
     * Since we are rendering the `UserOccupationGoalHeader` with server-side data,
     * we need to pass in the `seenOrgOrPPGoalHeader` user setting (in addition to
     * the `userOccupationInfo` itself). This determines what text to show.
     * This setting is stored as 'on' (true) or 'off' (false) in the DB.
     */
    const hasSeenUserGoalHeader =
        (appContext.udData.me.settings as unknown as {seenOrgOrPPGoalHeader: string})
            .seenOrgOrPPGoalHeader === 'on'
            ? true
            : false;

    // Initialize root store
    const [rootStore] = useState(
        () =>
            new RootStore({
                billboardData: priorityData?.billboardData,
                brand: appContext.udData.Config.brand,
                contextData: priorityData?.loggedInContextData,
                currency: appContext.udData.Config.price_country?.currency || '',
                footerData: appContext.footer,
                locale: fullLocale,
                hasSeenUserGoalHeader,
                headerData: appContext.header,
                userOccupationInfo: priorityData?.userOccupationInfo,
                userEnrollments: priorityData?.userEnrollments,
                accelerator: priorityData?.accelerator ?? undefined,
                recommendedExam: priorityData?.recommendedExam,
                topicSlug: priorityData?.topicSlug,
                bundle: priorityData?.bundle,
                certification: priorityData?.certification,
            }),
    );
    const [integrationsStore] = useState(() => new IntegrationsStore());

    // Shim device types
    const {isMobile, isDesktop, isTablet} = useDevice();

    // local UD data state
    const [udData, setUDData] = useState(() => {
        const initUDData = mergeWithArrayOverwrite(appContext.udData, {
            Config: {
                app_name: process.env.APP_NAME,
                features: {
                    notice: {
                        smart_bar: true,
                    },
                },
            } as unknown as UDDataConfig,
            userAgnosticTrackingParams: {
                page_key: pageProps.pageKey,
            },
            isGlobalMeContextLoading: true,
            request: {
                isMobile: isMobile,
                isPC: isDesktop,
                isTablet: isTablet,
                language: locale,
                locale: fullLocale,
            },
        });
        return initUDData;
    });

    // Shim UD.performance global API
    shimUDPerformance();

    useEffect(() => {
        async function bootstrapClient() {
            // Get client UD data.  If we are in SSR mode, the data is already available in scope
            const {udData: clientUDData} =
                renderMode === RENDER_MODES.SSR ? await {udData} : await getClientAppContext();

            const fullUdData: UDData = mergeWithArrayOverwrite(udData, clientUDData);

            // Ensure we've set user context has loaded flag
            const fullUdDataWithOverrides: UDData = mergeWithArrayOverwrite(fullUdData, {
                isGlobalMeContextLoading: false,
            });

            // Update udData
            setUDData(fullUdDataWithOverrides);

            // If you are SSRing the page, you need to write global UD data to the window object.
            // This will be eventually replaced by a standard app context provider.
            if (renderMode === RENDER_MODES.SSR) {
                const appContextData = {
                    footer: appContext.footer,
                    header: appContext.header,
                    udData: fullUdDataWithOverrides,
                };
                Object.assign(window, {
                    [APP_CONTEXT_VAR_NAME]: appContextData,
                });
            }

            // Initialize application stores
            integrationsStore.initializeClient();

            // Turn on keyboard navigation detection
            detectKeyboardNavigation();

            return fullUdDataWithOverrides;
        }

        bootstrapClient();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // Use the layout defined at the page level, if available
    // https://nextjs.org/docs/basic-features/layouts#per-page-layouts
    const getLayout = Component.getLayout ?? ((page) => page);

    return (
        <>
            <I18nProvider lang={locale} locale={fullLocale} translations={pageProps.translations}>
                <UDDataProvider data={udData as UDData} writeToGlobal={true}>
                    <CommonStoresProvider>
                        <StoreProvider stores={[rootStore, integrationsStore]}>
                            <QueryClientProvider client={queryClient}>
                                <Hydrate state={pageProps.dehydratedState}>
                                    <ExperimentationProvider features={experimentationFeatures}>
                                        <BindI18nGlobals />
                                        <I18nAccessBootstrapper />
                                        <FbtBundleConfigProvider>
                                            {getLayout(<Component {...pageProps} />)}
                                        </FbtBundleConfigProvider>
                                    </ExperimentationProvider>
                                </Hydrate>
                            </QueryClientProvider>
                        </StoreProvider>
                    </CommonStoresProvider>
                </UDDataProvider>
            </I18nProvider>
        </>
    );
}

// Do not resolve anything user specific in this function.
App.getInitialProps = async function (appContext: AppContext) {
    /**
     * Required step from https://nextjs.org/docs/advanced-features/custom-app#caveats
     *
     * When you add getInitialProps in your custom app, you must import App from "next/app",
     * call App.getInitialProps(appContext) inside getInitialProps and merge the returned object into the return value.
     */
    const locale = appContext.router.locale as string;
    const fullLocale =
        i18nPathToBundleMap[locale as keyof typeof i18nPathToBundleMap] ??
        i18nPreferred.at(0) ??
        'en_US';
    const [nextAppContext] = await Promise.all([NextApp.getInitialProps(appContext)]);
    return {
        ...nextAppContext,
        locale,
        pageProps: {
            ...nextAppContext.pageProps,
            direction: resolveLocaleDirection(fullLocale as UdemyLocale),
        },
    };
};
